import { useEffect, useState } from "react";
import { IMondayTaskController } from "../controller";
import { useIntegrationsProvider } from "frontend/hooks/use-integrations-provider";
import { MondayColumn, MondayColumnInMonday } from "shared/integrations/mapper";
import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";
import { useMondayItem } from "frontend/canvas-designer-new/integrations/hooks/use-monday-item";
import { COLUMN_BLACKLIST } from "../consts";

interface UseMondayTaskControllerSetupOptions {
  controller: IMondayTaskController;
  integrationId: string;
  mondayItemId: string;
}

/**
 * Hook to set up the Monday task controller with integration data.
 * Loads integration items and sets board columns and mutate function on the controller.
 */
export function useMondayTaskControllerSetup({
  controller,
  integrationId,
  mondayItemId,
}: UseMondayTaskControllerSetupOptions) {
  const { element } = controller;
  const { expanded, showAllUpdates, hiddenColumns, pinnedColumns, expandedUpdateId, hiddenFiles } = element;
  const [boardColumns, setBoardColumns] = useState<MondayColumn[]>([]);

  const { getBoardIntegrationConfig, loadItems } = useIntegrationsProvider();
  const { data, isLoading, error, mutate } = useMondayItem(mondayItemId);
  const memoizedData = useDeepEqualMemo(data);
  const hasData = !!memoizedData;

  // Load item in integrations provider -> Ensures board columns are available.
  // TODO: This is legacy code and we should make a useMondayBoard or something...
  useEffect(() => {
    if (integrationId && mondayItemId) {
      loadItems({ [integrationId]: [mondayItemId] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId, mondayItemId]);

  // 1. Set the board columns on the controller, once the integration config loads
  useEffect(() => {
    const integrationConfig = getBoardIntegrationConfig(integrationId);
    if (integrationConfig?.columns) {
      const parsedColumns: MondayColumn[] = integrationConfig.columns
        .filter((c: MondayColumnInMonday) => !COLUMN_BLACKLIST.has(c.type))
        .map((c: MondayColumnInMonday) => ({
          ...c,
          settings: JSON.parse(c.settings_str ?? "null"),
        }));

      controller.setBoardColumns(parsedColumns);
      setBoardColumns(parsedColumns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId, getBoardIntegrationConfig]);

  // 2. Set the mutate function on the controller
  useEffect(() => {
    controller.setMutateFunction(mutate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId, mondayItemId]);

  // 3. Set the item on the controller, once the item data loads
  useEffect(() => {
    if (memoizedData && !isLoading) {
      controller.setItem(memoizedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedData, isLoading]);

  // 4. Set error state if it occurs
  useEffect(() => {
    if (error) {
      // error is Error & { code: MondayErrorCodes; url?: string }
      const errorCode = error.code;
      const errorUrl = error.url;
      controller.setError(errorCode, errorUrl);
    } else {
      controller.setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, controller]);

  /**
   * Update the height of the controller when the element changes
   * This is in an effect instead of on the controller to ensure that there
   * are no race conditions, and that the height is updated after the element.
   * Additionally, this handled cases where the controller itself did not trigger
   * the height update, for example through the toolbar
   */
  useEffect(() => {
    controller.updateHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasData,
    isLoading,
    expanded,
    showAllUpdates,
    hiddenColumns,
    pinnedColumns,
    expandedUpdateId,
    hiddenFiles,
    boardColumns,
  ]);
}
